import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import useUser from '@/hooks/useUser';

export const NOTIFICATION_SOUND_NAMES = [
  'classic',
  'concise',
  'confident',
  'point_blank',
  'self_assured',
  'slick',
] as const;

export type NotificationSoundName = (typeof NOTIFICATION_SOUND_NAMES)[number];

export const DEFAULT_NOTIFICATION_SOUND: NotificationSoundName = 'classic';

export const getNotificationSoundUrl = (name: NotificationSoundName): string =>
  `/notification-sounds/${name}.mp3`;

export const isNotificationSoundName = (value: unknown): value is NotificationSoundName =>
  typeof value === 'string' && (NOTIFICATION_SOUND_NAMES as readonly string[]).includes(value);

export type NotificationSound = { id: NotificationSoundName; name: string; url: string };

const useNotificationSounds = () => {
  const { t } = useTranslation();
  const { user, updateUserAsync } = useUser();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const sounds = useMemo<NotificationSound[]>(
    () =>
      NOTIFICATION_SOUND_NAMES.map((id) => ({
        id,
        name: t(`notifications.notification_sound_${id}`),
        url: getNotificationSoundUrl(id),
      })),
    [t]
  );

  const rawCurrent = user?.metadata?.notificationSound;
  const currentSound: NotificationSoundName | undefined = isNotificationSoundName(rawCurrent)
    ? rawCurrent
    : undefined;

  const setSound = useCallback(
    async (name: NotificationSoundName) => {
      if (!user) {
        return;
      }
      await updateUserAsync({ ...user, metadata: { ...user.metadata, notificationSound: name } });
    },
    [user, updateUserAsync]
  );

  const playUrl = useCallback((url: string) => {
    let audio = audioRef.current;
    if (!audio) {
      audio = new Audio();
      audioRef.current = audio;
    }
    audio.pause();
    audio.currentTime = 0;
    audio.src = url;
    const promise = audio.play();
    if (promise !== undefined) {
      promise.catch((error) => {
        console.error(error);
      });
    }
  }, []);

  const play = useCallback(() => {
    if (user?.metadata?.chatSound === false) {
      return;
    }
    const name = currentSound ?? DEFAULT_NOTIFICATION_SOUND;
    playUrl(getNotificationSoundUrl(name));
  }, [user, currentSound, playUrl]);

  const preview = useCallback(
    (name: NotificationSoundName) => {
      playUrl(getNotificationSoundUrl(name));
    },
    [playUrl]
  );

  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    },
    []
  );

  return { sounds, currentSound, setSound, play, preview };
};

export default useNotificationSounds;
